import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, vShow as _vShow, createVNode as _createVNode, withDirectives as _withDirectives, Transition as _Transition, withCtx as _withCtx, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, withModifiers as _withModifiers, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["onDrop", "onDragenter", "onDragleave"]
const _hoisted_2 = ["onDrop"]
const _hoisted_3 = { class: "preview-area__canvas-transformer" }
const _hoisted_4 = { class: "main-view__export-overlay" }

import { ComponentPublicInstance, computed, onMounted, ref, watch, inject } from 'vue'
import PreviewBody from '@/components/preview/PreviewBody.vue'
import PreviewFooter from '@/components/preview/PreviewFooter.vue'
import ActionLayer from '@/components/ActionLayer.vue'
import SnackbarContainer from '@/components/snackbar/SnackbarContainer.vue'
import useAudioEngine from '@/composables/UseAudioEngine'
import PreviewCanvas from '@/components/preview/PreviewCanvas.vue'
import { Coordinates, Orientation } from '@/types'
import { storeToRefs } from 'pinia'
import { useProjectStore, resolutionSizes } from '@/store/project-store'
import { ExportStatus, useExportStore } from '@/store/export-store'
import { ActionMenuTabs, useApplicationStore } from '@/store/application-store'
import HeaderNav from '@/components/nav/HeaderNav.vue'
import ExportModal from '@/components/export/ExportModal.vue'
import OnboardingTooltips from '@/components/onboarding/OnboardingTooltips.vue'
import * as PIXI from 'pixi.js'
import { CanvasRecorder } from '@/pixi/canvas-recorder'
import { useFileValidator, ValidationError } from '@/composables/UseFileValidator'
import useImageStates from '@/composables/UseImageStates'
import FFmpegUtility from '@/utils/ffmpeg-utility'
import AudioIntensityAnalyzer from '@/audio/AudioIntensityAnalyzer'
import { Sws } from '@serato/sws-sdk'
import { audioExtensionByMimeType } from '@/static/file-types'


export default /*#__PURE__*/_defineComponent({
  __name: 'MainView',
  props: {
    isViewLoaded: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
  isViewLoaded: boolean
};

_useCssVars(_ctx => ({
  "18d1b0fb": (previewBodyHeight.value),
  "4f50791c": (previewBodyWidth.value),
  "1d09f81e": (_unref(resolutionSizes).portrait.width),
  "2416805c": (_unref(resolutionSizes).portrait.height),
  "64ac0e55": (_unref(resolutionSizes).landscape.width),
  "1706c278": (_unref(resolutionSizes).landscape.height),
  "44cb211a": (_unref(resolutionSizes).square.width),
  "8836f5cc": (_unref(resolutionSizes).square.height)
}))

const showOnboardingTooltips = ref(true)
const isDragged = ref(false)
const showControls = ref(false)
const loadingFileName = ref('')
const exportModalOpen = ref(false) 
const applicationStore = useApplicationStore()
const { snackbarMessages } = storeToRefs(applicationStore)
const dismissMessage = applicationStore.dismissSnackbarMessage.bind(applicationStore)
const canvasRecorder = ref<CanvasRecorder>()
const canvas = ref<ComponentPublicInstance | null>(null)
const previewBodyHeight = ref(250)
const previewBodyWidth = ref(250)
const previewBodyRef = ref<ComponentPublicInstance | null>(null)

const projectStore = useProjectStore()
const exportStore = useExportStore()
const { orientation } = storeToRefs(projectStore)
const sws: Sws | undefined = inject('$swsSdk')



onMounted(() => {
  if (previewBodyRef.value?.$el) {
    new ResizeObserver((entries: ResizeObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.contentRect.height) {
          const newHeight = entry.contentRect.height
          if (newHeight !== previewBodyHeight.value) {
            previewBodyHeight.value = newHeight
          }
        }

        if (entry.contentRect.width) {
          const newWidth = entry.contentRect.width
          if (newWidth !== previewBodyWidth.value) {
            previewBodyWidth.value = newWidth
          }
        }
      })
    }).observe(previewBodyRef.value.$el)
  }
  downloadFileFromCloudLib()
})

const onOpenVideoExport = () => {
  muteAudio()
  exportModalOpen.value = true
  if (!canvasRecorder.value) {
    throw new Error('Cannot export as canvas recorder is not initialised')
  }
  
  if (audioBinary.value) {
    AudioIntensityAnalyzer.resetWeightedIntensity()
    canvasRecorder.value.startRecordCanvas(
      fileName.value, audioBinary.value, untrimmedAudioDurationSeconds.value, loopStartTime.value, loopEndTime.value
    ).catch(e => {
      console.error(e)
      exportStore.exportStatus = ExportStatus.ERROR
      applicationStore.pushSnackbarMessage({
        header: 'Oops something went wrong',
        text: 'Export failed',
        state: 'error'
      })
    })
  } else {
    throw new Error('Cannot export as audioBinary is undefined')
  }
}

const onCloseVideoExport = () => {
  unmuteAudio()
  exportModalOpen.value = false
}

const {
  addAudioFile,
  loadAudioURL,
  playAudio,
  stopAudio,
  loadState,
  muteAudio,
  unmuteAudio,
  fileName,
  audioBinary,
  untrimmedAudioDurationSeconds,
  loopEndTime,
  loopStartTime,
  volumeGain
} = useAudioEngine()

const {
  validateBackgroundFile,
  validateStickerFile
} = useFileValidator()

const { addLogoSticker, addSticker } = useImageStates()

const isAudioLoading = computed(() => {
  return loadState.value === 'loading'
})

const onInitPixiApplication = (application: PIXI.Application) => {
  const ffmpegUtility = FFmpegUtility.getInstance()
  ffmpegUtility.ffmpegLoadedPromise?.catch(error => {
    if (error instanceof RangeError) {
      //Some low end machines unable to load the ffmpeg due to low memeory.
      console.error(error)
      applicationStore.hasEnoughMemory = false
    } else {
      throw error
    }
  })
  canvasRecorder.value = new CanvasRecorder(ffmpegUtility, PIXI.Ticker.shared, application)
}

const onOrientationSelected = (selectedOrientation: Orientation) => {
  orientation.value = selectedOrientation
}

let showControlsTimeout: ReturnType<typeof setTimeout>

watch(loadState, (newLoadState) => {
  if (showControls.value) {
    if (newLoadState === 'loaded') {
      stopAudio()
      playAudio()
      applicationStore.pushSnackbarMessage({
        state: 'success',
        header: 'Audio loaded successfully'
      })
    } else if (newLoadState === 'unloaded') {
      // when the project resets
      showControls.value = false
    }
  } else {
    if (newLoadState === 'loaded') {
      showControlsTimeout = setTimeout(() => {
        showControls.value = true
        playAudio()
      }, 1500)
    } else {
      clearTimeout(showControlsTimeout)
      showControls.value = false
    }
  }
})

const onFileSelected = async (file: File | string, fileName?: string) => {
  applicationStore.clearSnackbarMessages()
  try {
    if (typeof file === 'string') {
      loadingFileName.value = fileName ?? file.substring(file.lastIndexOf('/') + 1)
      await loadAudioURL(file, loadingFileName.value)
    } else {
      loadingFileName.value = fileName ?? file.name
      await addAudioFile(file)
    }
  } catch (e) {
    showSnackBarError(e)
  }
}

const onDragEnter = () => {
  isDragged.value = true
}

const onDragLeave = (dragEvent: DragEvent) => {
  // This guard is required as 'dragleave' events are emitted
  // if the drag goes over a child element. In this case, we don't care
  // about this, only if the drag exits the window
  if (dragEvent.relatedTarget === null) {
    isDragged.value = false
  }
}

// For landing screen audio upload
const onDropInWindow = (drop: DragEvent) => {
  isDragged.value = false
  if (loadState.value !== 'loading' && !showControls.value) {
    const file = drop.dataTransfer?.files.item(0)

    if (drop.dataTransfer?.files.length === 1 && file) {
      onFileSelected(file)
    } else {
      showSnackBarError()
    }
  }
}

const onDropInCanvas = async (drop: DragEvent) => {
  isDragged.value = false
  if (showControls.value && canvas.value && drop.dataTransfer) {
    const file = drop.dataTransfer.files.item(0)
    const dropSrc = drop.dataTransfer.getData('src')

    // From gallery
    if (dropSrc) {
      switch (applicationStore.activeActionTab) {
      case ActionMenuTabs.BACKGROUND: {
        projectStore.setBackground(dropSrc)
        break
      } 
      case ActionMenuTabs.STICKERS: {
        if (applicationStore.isStickerLoading) {
          return
        }
        const coordinates = getImageDropCoordinates(drop)
        addSticker(dropSrc, coordinates)
        break
      }
      }
      return
    }

    // From system
    try {
      if (drop.dataTransfer.files.length !== 1 || !file) {
        throw new ValidationError('Oops something went wrong')
      }

      if (file.type.startsWith('audio/')) {
        onFileSelected(file)
      } else {
        switch (applicationStore.activeActionTab) {
        case ActionMenuTabs.BACKGROUND: {
          await validateBackgroundFile(file)
          projectStore.setBackground(URL.createObjectURL(file), 'fill', file.name)
          break
        } 
        case ActionMenuTabs.STICKERS: {
          validateStickerFile(file)
          const coordinates = getImageDropCoordinates(drop)
          addLogoSticker(URL.createObjectURL(file), file.name, coordinates)
          break
        }
        default: {
          // Display audio errors for other tabs
          onFileSelected(file)
        }
        }
      }
    } catch (e) {
      showSnackBarError(e)
    }
  }
}

const showSnackBarError = (e?: Error | unknown) => {
  if (e instanceof ValidationError) {
    applicationStore.pushSnackbarMessage({
      header: e.title,
      text: e.message,
      state: 'error'
    })
  } else {
    applicationStore.pushSnackbarMessage({
      state: 'error',
      header: 'Oops something went wrong'
    })
  }
}

/**
 * Gets the x,y coordinates to place the sticker after getting dropped onto the canvas
 * If dropped outside the canvas area, the default position is the centre of the canvas
 * @param event the DragEvent onDrop
 * @return sticker coordinates anchored on the centre of the sticker
 */
const getImageDropCoordinates = (event: DragEvent): Coordinates => {
  // Get the first child which is the canvas boundary excluding controls
  const isDroppedInCanvas = canvas.value ? canvas.value.$el.childNodes[0].contains(event.target) : false
  const x = isDroppedInCanvas ? event.offsetX : projectStore.resolution.width / 2
  const y = isDroppedInCanvas ? event.offsetY : projectStore.resolution.height / 2

  return { x, y }
}

const downloadFileFromCloudLib = async () => {
  const queryParams = new URLSearchParams(window.location.search)
  if (queryParams.has('file_id') && sws) {
    const fileId = queryParams.get('file_id') ?? ''
    try {
      // Get file id from the URL
      const response = await sws.cloudlib.getFile({ fileId })
      const downloadFileUrl = response?.download.request.url
      const fileType = audioExtensionByMimeType[response.mime_type]
      if (downloadFileUrl) {
        // Initially set to mute because of the browser's Autoplay policy.
        muteAudio()
        // Set gain 0 to change the volume and slider to 0.
        volumeGain.value = 0.0
        onFileSelected(downloadFileUrl, `studio-track.${fileType}`)
        // URL param/fragment is removed from URL after being read.
        const url = new URL(window.location.href)
        url.searchParams.delete('file_id')
        window.history.replaceState(null, '', url.toString())
      }
    } catch (e) {
      applicationStore.pushSnackbarMessage({
        header: 'Audio file not found',
        text: 'Try exporting again from Studio or manually upload your file.',
        state: 'error'
      })
    }
  }
}


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "main-view",
    key: _unref(projectStore).projectId,
    onDragover: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["prevent"])),
    onDrop: _withModifiers(onDropInWindow, ["prevent"]),
    onDragenter: _withModifiers(onDragEnter, ["prevent"]),
    onDragleave: _withModifiers(onDragLeave, ["prevent"])
  }, [
    _createElementVNode("div", {
      class: "main-view__preview-area preview-area",
      onDrop: _withModifiers(onDropInCanvas, ["prevent"])
    }, [
      _createVNode(_Transition, { name: "header-slide" }, {
        default: _withCtx(() => [
          _withDirectives(_createVNode(HeaderNav, { showForumLink: showControls.value }, null, 8, ["showForumLink"]), [
            [_vShow, __props.isViewLoaded]
          ])
        ]),
        _: 1
      }),
      _createVNode(_Transition, { name: "main-view__fade-transition" }, {
        default: _withCtx(() => [
          _withDirectives(_createVNode(PreviewBody, {
            ref_key: "previewBodyRef",
            ref: previewBodyRef,
            class: "preview-area__preview-body",
            "show-prompt": !showControls.value,
            "is-dragged": isDragged.value,
            "load-state": _unref(loadState),
            "file-name": loadingFileName.value,
            onFileSelected: onFileSelected
          }, {
            canvas: _withCtx(() => [
              _createElementVNode("div", {
                class: _normalizeClass(['preview-area__canvas-wrapper', `preview-area__canvas-wrapper--${_unref(orientation)}`])
              }, [
                _createElementVNode("div", _hoisted_3, [
                  _createVNode(PreviewCanvas, {
                    ref_key: "canvas",
                    ref: canvas,
                    class: "preview-area__canvas",
                    "show-canvas-controls": showControls.value,
                    onInitPixiApplication: onInitPixiApplication
                  }, null, 8, ["show-canvas-controls"])
                ])
              ], 2)
            ]),
            _: 1
          }, 8, ["show-prompt", "is-dragged", "load-state", "file-name"]), [
            [_vShow, __props.isViewLoaded]
          ])
        ]),
        _: 1
      }),
      _createVNode(_Transition, { name: "main-view__fade-transition" }, {
        default: _withCtx(() => [
          _withDirectives(_createVNode(PreviewFooter, {
            class: "preview-area__preview-footer",
            "show-controls": showControls.value,
            "is-audio-loading": _unref(isAudioLoading),
            "track-name": _unref(fileName),
            orientation: _unref(orientation),
            onOrientationSelected: onOrientationSelected,
            onFileSelected: onFileSelected,
            onOpenVideoExport: onOpenVideoExport,
            onToggleWaveform: _cache[0] || (_cache[0] = ($event: any) => (showOnboardingTooltips.value = false))
          }, null, 8, ["show-controls", "is-audio-loading", "track-name", "orientation"]), [
            [_vShow, __props.isViewLoaded]
          ])
        ]),
        _: 1
      }),
      _createVNode(SnackbarContainer, {
        class: "preview-area__snackbar-container",
        messages: _unref(snackbarMessages),
        onDismiss: _unref(dismissMessage)
      }, null, 8, ["messages", "onDismiss"])
    ], 40, _hoisted_2),
    _createVNode(_Transition, { name: "main-view__action-layer-slide-transition" }, {
      default: _withCtx(() => [
        _withDirectives(_createVNode(ActionLayer, {
          class: _normalizeClass(['main-view__action-layer', { 'main-view__action-layer--disabled': !showControls.value }])
        }, null, 8, ["class"]), [
          [_vShow, __props.isViewLoaded]
        ])
      ]),
      _: 1
    }),
    _withDirectives(_createElementVNode("div", _hoisted_4, null, 512), [
      [_vShow, exportModalOpen.value]
    ]),
    _withDirectives(_createVNode(ExportModal, {
      class: _normalizeClass(['main-view__export-modal', {'main-view__export-modal--open': exportModalOpen.value }]),
      "is-modal-open": exportModalOpen.value,
      "canvas-recorder": canvasRecorder.value,
      onCloseVideoExport: onCloseVideoExport
    }, null, 8, ["class", "is-modal-open", "canvas-recorder"]), [
      [_vShow, showControls.value]
    ]),
    (showControls.value && showOnboardingTooltips.value)
      ? (_openBlock(), _createBlock(OnboardingTooltips, { key: 0 }))
      : _createCommentVNode("", true)
  ], 40, _hoisted_1))
}
}

})